import cx from "classnames";
import type { KeyboardEvent } from "react";
import { FC, SVGProps } from "react";

import { TextButton } from "atoms/text-button/TextButton";

type Props = {
  "className"?: string;

  "type"?: "text" | "tel" | "number" | "password" | "search" | "email" | "decimal";

  /** This is both the input label and the placeholder text */
  "label"?: string;
  "aria-label"?: string;

  /** Additional text that will appear below the input field */
  "supportingText"?: string | JSX.Element;
  "secondarySupportingText"?: string;

  "placeholder"?: string;

  "invalid"?: boolean;
  "value"?: string;
  "disabled"?: boolean;
  "readOnly"?: boolean;
  "name"?: string;
  "onChange"?: (value: string) => void;
  "inputMode"?: "text" | "numeric" | "tel" | "email" | "url" | "search";
  "maxLength"?: number;
  "autoFocus"?: boolean;
  "autoComplete"?: string;
  "pattern"?: string;
  "onFocus"?: (value: string) => void;
  "onBlur"?: (value: string) => void;
  "onKeyUp"?: (value: string) => void;
  "onKeyDown"?: (event: KeyboardEvent<HTMLInputElement>) => void | undefined;

  /** SVG icon to render on the left side of the input box */
  "leftIcon"?: FC<SVGProps<SVGSVGElement>>;

  /** SVG icon to render on the right side of the input box */
  "rightIcon"?: FC<SVGProps<SVGSVGElement>> | string;

  /** Handler that is called when either the left or right icons are clicked */
  "onIconClick"?: (side: "left" | "right") => void;

  /** Specify the testid for current component */
  "data-testid"?: string;

  /** Specify the className for the label */
  "labelClassName"?: string;

  /** Specify the className for the placeholder */
  "placeholderClassName"?: string;
};

export const TextField = ({
  className,
  labelClassName,
  placeholderClassName,
  type = "text",
  label,
  "aria-label": ariaLabel,
  name,
  supportingText,
  secondarySupportingText,
  value,
  onChange,
  invalid = false,
  disabled = false,
  readOnly = false,
  leftIcon: Left,
  rightIcon: Right,
  inputMode,
  maxLength,
  onIconClick,
  placeholder,
  autoFocus = false,
  autoComplete,
  pattern,
  onFocus,
  onBlur,
  onKeyUp,
  onKeyDown,
  "data-testid": dataTestId,
}: Props) => {
  const modes = {
    tel: "tel",
    number: "numeric",
    text: "text",
    password: "text",
    search: "search",
    email: "email",
    decimal: "decimal",
  } as const;

  const isTextButton = typeof Right === "string";

  return (
    <label
      className={cx(
        "relative block pt-2 text-body1 text-secondary",
        (disabled || readOnly) && "opacity-60",
      )}
    >
      {Left && (
        <Left
          className="absolute top-[50%] mt-1 h-6 w-6 -translate-y-[50%] translate-x-[50%] cursor-pointer"
          onClick={() => onIconClick?.("left")}
        />
      )}
      <input
        className={cx(
          "peer block h-12 w-full appearance-none rounded-lg border-none bg-white text-primary-neutral [color-scheme:light] placeholder:text-transparent focus:!outline-none focus:!ring-0 focus:placeholder:!text-secondary",
          Left ? "pl-12" : "px-4",
          Right ? "pr-12" : "",
          className,
        )}
        placeholder={placeholder || " "}
        type={type}
        aria-label={ariaLabel}
        inputMode={inputMode || modes[type]}
        aria-invalid={invalid}
        disabled={disabled}
        readOnly={readOnly}
        value={value}
        name={name}
        onChange={(e) => onChange?.(e.target.value)}
        maxLength={maxLength}
        data-testid={dataTestId}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        pattern={pattern}
        onFocus={(e) => onFocus?.(e.target.value)}
        onBlur={(e) => onBlur?.(e.target.value)}
        onKeyUp={(e) => onKeyUp?.(e.key)}
        onKeyDown={(e) => onKeyDown?.(e)}
      />
      {Right && !isTextButton && (
        <Right
          className="absolute right-3 top-5 h-6 w-6 cursor-pointer peer-aria-invalid:fill-red"
          onClick={() => onIconClick?.("right")}
        />
      )}
      {Right && isTextButton && (
        <div className="absolute right-2 top-[0.80rem] cursor-pointer">
          <TextButton onClick={() => onIconClick?.("right")}>{Right}</TextButton>
        </div>
      )}
      <span
        className={cx(
          "absolute left-3 top-0 whitespace-nowrap bg-white px-1 text-xs text-secondary peer-placeholder-shown:invisible peer-focus:!visible peer-focus:text-brand-color peer-aria-invalid:text-red",
          labelClassName,
        )}
      >
        {label}
      </span>
      <span
        className={cx(
          "absolute left-3 top-5 cursor-text whitespace-nowrap bg-white px-1 text-body1 text-secondary peer-focus:!invisible peer-aria-invalid:text-red",
          value && "hidden",
          Left && "ml-8",
          placeholderClassName,
          invalid && "hidden",
        )}
      >
        {label}
      </span>
      {supportingText && (
        <span className="block px-4 pt-1 text-body3 peer-aria-invalid:text-red">
          {supportingText}
        </span>
      )}
      {secondarySupportingText && (
        <div className="block px-4 pt-1 text-body3 peer-aria-invalid:text-red">
          {secondarySupportingText}
        </div>
      )}
    </label>
  );
};
